import React, { useState } from 'react';
import { userFeedbackAPI, UserFeedbackRequest } from '../../services/api';
import './FeedbackButton.css';

type Category = NonNullable<UserFeedbackRequest['category']>;

// Public community group — also linked from the bot Contact us flow.
const COMMUNITY_GROUP_URL = 'https://t.me/lomicommunity';

const CATEGORIES: { value: Category; label: string }[] = [
  { value: 'bug', label: 'Something is broken' },
  { value: 'content', label: 'Wrong / unclear content' },
  { value: 'account', label: 'Account help' },
  { value: 'other', label: 'Other' },
];

/**
 * Floating "Contact us" FAB. Opens an inline form that posts directly to
 * /user-feedback. Labeled "Contact us" because students get confused
 * by "Feedback" — they read it as "opinion on product" rather than
 * "reach the team". Backend endpoint name is kept for back-compat.
 */
const FeedbackButton: React.FC = () => {
  const [open, setOpen] = useState(false);
  const [category, setCategory] = useState<Category>('bug');
  const [message, setMessage] = useState('');
  const [contact, setContact] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [done, setDone] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const close = () => {
    setOpen(false);
    // Reset after the popover hides so the user doesn't see the form blank.
    setTimeout(() => {
      setMessage('');
      setContact('');
      setCategory('bug');
      setDone(false);
      setError(null);
    }, 200);
  };

  const submit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!message.trim() || submitting) return;
    setSubmitting(true);
    setError(null);
    try {
      await userFeedbackAPI.submit({
        surface: 'web',
        category,
        message: message.trim(),
        contact: contact.trim() || undefined,
        location_ref: window.location.pathname + window.location.search,
      });
      setDone(true);
    } catch (err: any) {
      setError(err?.response?.data?.detail || err?.message || 'Failed to send. Try again?');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="lomi-feedback-button">
      {open && (
        <div className="lomi-feedback-popover" role="dialog" aria-label="Contact us">
          <div className="lomi-feedback-popover__header">
            <strong>Contact us 💬</strong>
            <button
              type="button"
              className="lomi-feedback-popover__close"
              onClick={close}
              aria-label="Close"
            >
              ✕
            </button>
          </div>

          {done ? (
            <>
              <p className="lomi-feedback-popover__lead" style={{ color: '#a3e635' }}>
                ✓ Got it — thank you. We read every message.
              </p>
              <a
                className="lomi-feedback-popover__link"
                href={COMMUNITY_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                💬 Meanwhile, join our community group
              </a>
              <button
                type="button"
                className="lomi-feedback-popover__submit"
                onClick={close}
              >
                Close
              </button>
            </>
          ) : (
            <form onSubmit={submit}>
              <p className="lomi-feedback-popover__lead">
                Goes straight to the team — we read every message.
              </p>

              <a
                className="lomi-feedback-popover__link"
                href={COMMUNITY_GROUP_URL}
                target="_blank"
                rel="noopener noreferrer"
              >
                💬 Join our community group — ask other students
              </a>

              <label className="lomi-feedback-popover__label">What's this about?</label>
              <select
                className="lomi-feedback-popover__field"
                value={category}
                onChange={(e) => setCategory(e.target.value as Category)}
              >
                {CATEGORIES.map((c) => (
                  <option key={c.value} value={c.value}>{c.label}</option>
                ))}
              </select>

              <label className="lomi-feedback-popover__label">Message</label>
              <textarea
                className="lomi-feedback-popover__field"
                value={message}
                onChange={(e) => setMessage(e.target.value)}
                rows={4}
                maxLength={4000}
                placeholder="Tell us what happened, what you wish worked differently, or what you love."
                required
              />

              <label className="lomi-feedback-popover__label">
                Contact <span style={{ opacity: 0.6 }}>(optional)</span>
              </label>
              <input
                className="lomi-feedback-popover__field"
                type="text"
                value={contact}
                onChange={(e) => setContact(e.target.value)}
                maxLength={255}
                placeholder="Phone, email, or @telegram if you want a reply"
              />

              {error && (
                <p className="lomi-feedback-popover__error">{error}</p>
              )}

              <button
                type="submit"
                className="lomi-feedback-popover__submit"
                disabled={!message.trim() || submitting}
              >
                {submitting ? 'Sending…' : 'Send'}
              </button>
            </form>
          )}
        </div>
      )}
      <button
        type="button"
        className="lomi-feedback-button__fab"
        onClick={() => setOpen((v) => !v)}
        aria-label="Contact us"
        title="Contact us"
      >
        💬 Contact us
      </button>
    </div>
  );
};

export default FeedbackButton;
